import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  ChevronDown, ShoppingCart, User, LogOut, Package, Heart, Coins, NirCoinIcon, Bell, X
} from "../common/Icons";
import axios from "axios";
import { DesktopNav, MobileNav } from "./NavMenu";
import ThemeToggle from "../ThemeToggle";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Header = ({ 
  cartCount, 
  cartItems, 
  isLoggedIn, 
  user, 
  onLogin, 
  onLogout, 
  nirtcgBalance,
  onOpenAuthModal 
}) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef(null);
  const userMenuRef = useRef(null);

  const isAdmin = user?.role === 'admin';


  // Load notifications when logged in
  useEffect(() => {
    if (isLoggedIn && user?.user_id) {
      loadNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, user?.user_id]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) {
      console.error('Error loading notifications:', e);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (e) {
      console.error('Error marking notification as read:', e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error('Error marking all as read:', e);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'ranking_up': return '📈';
      case 'ranking_down': return '📉';
      case 'tournament': return '🏆';
      case 'purchase': return '🛒';
      case 'nircoin': return '💰';
      default: return '🔔';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return 'Ahora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-purple-900/30" data-testid="header">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="logo">
          <img 
            src="/logo-nirvana.png" 
            alt="Nirvana TCG Logo" 
            className="w-12 h-12 md:w-14 md:h-14 object-contain"
          />
          <div className="hidden sm:block">
            <h1 className="text-base md:text-lg font-bold bg-gradient-to-r from-red-500 via-pink-500 to-yellow-400 bg-clip-text text-transparent">
              NIRVANA TCG
            </h1>
            <p className="text-[9px] md:text-[10px] text-gray-400 tracking-wider">TRADING CARD GAMES</p>
          </div>
        </Link>

        <DesktopNav />

        {/* User Panel */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* NirTCGCoin Balance */}
          {/* Notifications Bell */}
          {isLoggedIn && (
            <div className="relative" ref={notifRef}>
              <button 
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className="relative p-2 rounded-full bg-purple-900/50 border border-purple-700 hover:bg-purple-800/50 transition-colors"
                data-testid="notifications-btn"
              >
                <Bell />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs font-bold text-white flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {isNotificationsOpen && (
                <div className="absolute top-full right-0 mt-2 w-80 md:w-96 bg-gray-900/95 backdrop-blur border border-purple-800 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="notifications-dropdown">
                  <div className="p-3 border-b border-purple-800/50 flex items-center justify-between bg-purple-900/30">
                    <span className="font-bold text-yellow-400">Notificaciones</span>
                    {unreadCount > 0 && (
                      <button 
                        onClick={markAllRead}
                        className="text-xs text-pink-400 hover:text-pink-300"
                      >
                        Marcar todo leído
                      </button>
                    )}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-gray-500 text-sm">
                        No tienes notificaciones
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div 
                          key={notif.id}
                          onClick={() => !notif.read && markAsRead(notif.id)}
                          className={`p-3 border-b border-purple-800/30 hover:bg-purple-900/30 cursor-pointer transition-colors ${!notif.read ? 'bg-purple-900/20' : ''}`}
                        >
                          <div className="flex gap-3">
                            <span className="text-2xl">{getNotificationIcon(notif.type)}</span>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-white text-sm">{notif.title}</p>
                              <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{notif.message}</p>
                              <p className="text-gray-500 text-xs mt-1">{formatTime(notif.created_at)}</p>
                            </div>
                            {!notif.read && (
                              <div className="w-2 h-2 bg-pink-500 rounded-full flex-shrink-0 mt-2"></div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User Dropdown */}
          <div className="relative" ref={userMenuRef}>
            <button 
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-1 md:gap-2 p-1.5 md:p-2 rounded-full bg-purple-900/50 border border-purple-700 hover:bg-purple-800/50 transition-colors"
              data-testid="user-menu-button"
            >
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center overflow-hidden">
                {isLoggedIn && user?.picture ? (
                  <img src={user.picture} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User />
                )}
              </div>
              <ChevronDown />
            </button>

            {isUserMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-72 md:w-80 bg-gray-900/95 backdrop-blur border border-purple-800 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="user-dropdown">
                {/* User Info */}
                <div className="p-4 border-b border-purple-800/50 bg-purple-900/30">
                  {isLoggedIn ? (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center overflow-hidden">
                        {user?.picture ? (
                          <img src={user.picture} alt="Avatar" className="w-full h-full object-cover" />
                        ) : (
                          <User />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white truncate">{user?.name || 'Usuario'}</p>
                        <p className="text-xs md:text-sm text-gray-400 truncate">{user?.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <NirCoinIcon size={14} />
                          <span className="text-xs md:text-sm font-bold text-yellow-400">{nirtcgBalance?.toFixed(2) || '0.00'} NirCoins</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-gray-300 mb-3 text-sm">Inicia sesión para acceder</p>
                      <button 
                        onClick={() => { onOpenAuthModal?.(); setIsUserMenuOpen(false); }}
                        className="w-full py-2 bg-gradient-to-r from-pink-500 to-red-500 rounded-lg font-semibold text-white hover:shadow-lg hover:shadow-pink-500/30 transition-all text-sm"
                        data-testid="login-btn"
                      >
                        Iniciar Sesión
                      </button>
                    </div>
                  )}
                </div>

                {/* Cart Section */}
                <div className="p-3 md:p-4 border-b border-purple-800/50">
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <span className="flex items-center gap-2 text-yellow-400 font-semibold text-sm">
                      <ShoppingCart size={16} />
                      Carrito
                    </span>
                    <span className="px-2 py-0.5 bg-pink-500 rounded-full text-xs font-bold text-white">
                      {cartCount}
                    </span>
                  </div>
                  {cartCount > 0 ? (
                    <div className="space-y-1.5 md:space-y-2 max-h-28 md:max-h-32 overflow-y-auto">
                      {cartItems.slice(0, 3).map((item, index) => (
                        <div key={index} className="flex items-center justify-between text-xs md:text-sm">
                          <span className="text-gray-300 truncate flex-1">{item.name}</span>
                          <span className="text-pink-400 ml-2">${item.price}</span>
                        </div>
                      ))}
                      {cartItems.length > 3 && (
                        <p className="text-gray-500 text-xs">+{cartItems.length - 3} más...</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-xs md:text-sm text-center">Tu carrito está vacío</p>
                  )}
                  <Link 
                    to="/cart" 
                    className="block mt-2 md:mt-3 text-center py-1.5 md:py-2 border border-yellow-400 text-yellow-400 rounded-lg hover:bg-yellow-400/10 transition-colors text-xs md:text-sm font-semibold"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    Ver Carrito Completo
                  </Link>
                </div>

                {/* Menu Options */}
                {isLoggedIn && (
                  <div className="p-2">
                    <Link to="/perfil" className="flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-purple-800/50 rounded-lg transition-colors text-sm" onClick={() => setIsUserMenuOpen(false)}>
                      <User /> Mi Perfil
                    </Link>
                    <Link to="/orders" className="flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-purple-800/50 rounded-lg transition-colors text-sm" onClick={() => setIsUserMenuOpen(false)}>
                      <Package /> Mis Pedidos
                    </Link>
                    <Link to="/wishlist" className="flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-purple-800/50 rounded-lg transition-colors text-sm" onClick={() => setIsUserMenuOpen(false)}>
                      <Heart /> Lista de Deseos
                    </Link>
                    <Link to="/wallet" className="flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-purple-800/50 rounded-lg transition-colors text-sm" onClick={() => setIsUserMenuOpen(false)}>
                      <Coins /> Mis NirCoins
                    </Link>
                    {user?.role === 'admin' && (
                      <Link to="/admin" className="flex items-center gap-3 px-3 py-2 text-yellow-400 hover:bg-yellow-500/20 rounded-lg transition-colors text-sm font-semibold" onClick={() => setIsUserMenuOpen(false)}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                        Panel Admin
                      </Link>
                    )}
                    <button 
                      onClick={() => { onLogout(); setIsUserMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors text-sm"
                    >
                      <LogOut /> Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-yellow-400"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900/95 border-t border-purple-800 py-4">
          <MobileNav onNavigate={() => setIsMobileMenuOpen(false)} />
        </div>
      )}

    </header>
  );
};

export default Header;
